import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { authService } from '../services/authService';
import { chargerService } from '../services/chargerService';
import { QueueAccessSettingsPanel } from '../components/admin/QueueAccessSettingsPanel';
import { User, Charger } from '../types';
import { 
  Users, 
  Zap, 
  Shield, 
  Settings, 
  UserCheck, 
  UserX, 
  Trash2,
  Search,
  Plus,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  LogIn
} from 'lucide-react';

interface PendingUser extends User {
  status: 0; // 0 = pending
}

export function AdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useApp();
  const [activeTab, setActiveTab] = useState<'users' | 'chargers' | 'sessions' | 'settings'>('users');
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [chargers, setChargers] = useState<Charger[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActioning, setIsActioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null);

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    loadAdminData();
  }, [activeTab]);

  const loadAdminData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (activeTab === 'users') {
        const [pending, users] = await Promise.all([
          authService.getPendingUsers(),
          authService.getAllUsers()
        ]);
        setPendingUsers(pending.filter(user => user.status === 0) as PendingUser[]); // 0 = pending
        setAllUsers(users);
      } else if (activeTab === 'chargers') {
        const chargerData = await chargerService.getChargers();
        setChargers(chargerData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admin data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveUser = async (userId: string) => {
    try {
      setIsActioning(true);
      await authService.approveUser(userId, { reason: 'Approved by admin' });
      await loadAdminData();
      setNotification({ message: 'User approved successfully! They have been notified.', type: 'success' });
    } catch (error) {
      console.error('Failed to approve user:', error);
      setNotification({ message: 'Failed to approve user. Please try again.', type: 'error' });
    } finally {
      setIsActioning(false);
    }
  };

  const handleRejectUser = async (userId: string, reason: string) => {
    try {
      setIsActioning(true);
      await authService.rejectUser(userId, { reason });
      await loadAdminData();
      setNotification({ message: 'User rejected successfully. They have been notified.', type: 'success' });
    } catch (error) {
      console.error('Failed to reject user:', error);
      setNotification({ message: 'Failed to reject user. Please try again.', type: 'error' });
    } finally {
      setIsActioning(false);
    }
  };

  const handleDeleteUser = async (userId: string, reason: string) => {
    try {
      setIsActioning(true);
      await authService.deleteUser(userId, { reason });
      await loadAdminData();
      setNotification({ message: 'User permanently deleted from the system.', type: 'success' });
    } catch (error) {
      console.error('Failed to delete user:', error);
      setNotification({ message: 'Failed to delete user. Please try again.', type: 'error' });
    } finally {
      setIsActioning(false);
    }
  };

  const handleImpersonateUser = async (user: User) => {
    try {
      setIsActioning(true);
      console.log('🔐 Impersonating user:', user.telegramId, user.fullName);
      
      // Backup the current admin token before switching
      const adminToken = authService.getToken();
      
      // Get the impersonation token from the backend
      const result = await authService.impersonate(user.telegramId);
      
      // Store impersonation data in sessionStorage (not persistent across browser close)
      const impersonationData = {
        impersonationToken: result.token,
        impersonatedUser: result.user,
        impersonatedAt: new Date().toISOString(),
        originalAdminTelegramId: state.user?.telegramId
      };
      
      sessionStorage.setItem('impersonationData', JSON.stringify(impersonationData));
      
      // Also backup the admin token for easy restoration
      if (adminToken) {
        sessionStorage.setItem('adminBackupToken', adminToken);
      }
      
      // Replace the current authentication token with the impersonation token
      authService.setToken(result.token);
      
      setNotification({ 
        message: `Impersonating ${result.user.fullName}. Redirecting to home screen...`, 
        type: 'success' 
      });
      
      // Navigate to home screen
      setTimeout(() => {
        navigate('/');
      }, 500);
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      setNotification({ 
        message: error instanceof Error ? error.message : 'Failed to impersonate user. Please try again.', 
        type: 'error' 
      });
    } finally {
      setIsActioning(false);
    }
  };

  const handleToggleChargerStatus = async (chargerId: string, online: boolean) => {
    try {
      setIsActioning(true);
      await chargerService.updateStatus(chargerId, { 
        isOnline: !online, 
        reason: online ? 'Disabled by admin' : 'Enabled by admin' 
      });
      await loadAdminData();
    } catch (error) {
      console.error('Failed to update charger status:', error);
    } finally {
      setIsActioning(false);
    }
  };

  const filteredUsers = allUsers.filter(user => 
    user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phoneE164.value.includes(searchTerm) ||
    user.primaryPlate.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredChargers = chargers.filter(charger =>
    charger.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    charger.slotNumber.toString().includes(searchTerm)
  );

  if (!state.user || state.user.role !== 2) { // 2 = admin
    return (
      <div className="error">
        <div className="card text-center">
          <Shield size={48} className="mb-4 text-destructive" />
          <h2>Access Denied</h2>
          <p>You don't have permission to access the admin panel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-page">
      <div className="modern-page-header">
        <div className="header-top">
          <div className="header-title-section">
            <div className="page-icon">
              <Shield size={28} />
            </div>
            <h1 className="page-title">{t('admin.title')}</h1>
            <p className="page-subtitle">Manage users, chargers, and system settings</p>
          </div>
          <button 
            className={`refresh-button ${isLoading ? 'refreshing' : ''}`}
            onClick={loadAdminData}
            disabled={isLoading}
          >
            <RefreshCw className={isLoading ? 'animate-spin' : ''} size={20} />
          </button>
        </div>
      </div>

      {/* Modern Tab Navigation */}
      <div className="modern-tabs">
        <button
          className={`btn-modern btn-secondary-modern modern-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users size={20} />
          <span>Users</span>
          {pendingUsers.length > 0 && (
            <span className="tab-badge">{pendingUsers.length}</span>
          )}
        </button>
        <button
          className={`btn-modern btn-secondary-modern modern-tab ${activeTab === 'chargers' ? 'active' : ''}`}
          onClick={() => setActiveTab('chargers')}
        >
          <Zap size={20} />
          <span>Chargers</span>
        </button>
        <button
          className={`btn-modern btn-secondary-modern modern-tab ${activeTab === 'sessions' ? 'active' : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          <Clock size={20} />
          <span>Sessions</span>
        </button>
        <button
          className={`btn-modern btn-secondary-modern modern-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <Settings size={20} />
          <span>{t('admin.tabs.settings')}</span>
        </button>
      </div>

      {/* Modern Search */}
      <div className="modern-search-section">
        <div className="modern-search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input-modern modern-search-input"
          />
        </div>
      </div>

      {error && (
        <div className="modern-error-inline">
          <div className="error-content">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
          <button className="btn-modern btn-secondary-modern btn-sm" onClick={loadAdminData}>
            Retry
          </button>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="modern-admin-section">
          {/* Pending Approvals */}
          {pendingUsers.length > 0 && (
            <div className="modern-section">
              <div className="section-header">
                <h3 className="section-title">
                  <AlertCircle size={20} />
                  Pending Approvals
                  <span className="count-badge">{pendingUsers.length}</span>
                </h3>
              </div>
              <div className="modern-cards-grid">
                {pendingUsers.map((user) => (
                  <div key={user.id} className="modern-user-card pending">
                    <div className="user-card-header">
                      <div className="user-avatar">
                        <Users size={20} />
                      </div>
                      <div className="user-basic-info">
                        <h4 className="user-name">{user.fullName}</h4>
                        <p className="user-contact">
                          <a 
                            href={`tel:${user.phoneE164.value.replace(/[^\d+]/g, '')}`}
                            className="phone-link inline"
                            title={`Call ${user.fullName}`}
                          >
                            {user.phoneE164.value}
                          </a> • {user.primaryPlate.value}
                        </p>
                      </div>
                      <div className="user-status-badge pending">
                        <Clock size={16} />
                        Pending
                      </div>
                    </div>
                    <div className="user-card-meta">
                      <span>@{user.username || 'N/A'}</span>
                      <span>Joined: {new Date(user.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="user-card-actions">
                      <button
                        className="btn-modern btn-success-modern"
                        onClick={() => handleApproveUser(user.id)}
                        disabled={isActioning}
                      >
                        <UserCheck size={16} />
                        Approve
                      </button>
                      <button
                        className="btn-modern btn-destructive-modern"
                        onClick={() => handleRejectUser(user.id, 'Rejected by admin')}
                        disabled={isActioning}
                      >
                        <UserX size={16} />
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Users */}
          <div className="modern-section">
            <div className="section-header">
              <h3 className="section-title">
                All Users
                <span className="count-badge">{filteredUsers.length}</span>
              </h3>
            </div>
            <div className="modern-cards-grid">
              {filteredUsers.map((user) => (
                <div key={user.id} className={`modern-user-card ${user.status === 1 ? 'approved' : user.status === 0 ? 'pending' : user.status === 2 ? 'rejected' : 'unknown'}`}>
                  <div className="user-card-header">
                    <div className="user-avatar">
                      <Users size={20} />
                    </div>
                    <div className="user-basic-info">
                      <h4 className="user-name">{user.fullName}</h4>
                      <p className="user-contact">
                        <a 
                          href={`tel:${user.phoneE164.value.replace(/[^\d+]/g, '')}`}
                          className="phone-link inline"
                          title={`Call ${user.fullName}`}
                        >
                          {user.phoneE164.value}
                        </a> • {user.primaryPlate.value}
                      </p>
                    </div>
                    <div className={`user-status-badge ${user.status === 1 ? 'approved' : user.status === 0 ? 'pending' : user.status === 2 ? 'rejected' : 'unknown'}`}>
                      {user.status === 1 && <CheckCircle size={16} />}
                      {user.status === 2 && <UserX size={16} />}
                      {user.status === 0 && <Clock size={16} />}
                      {user.status === 1 ? 'Approved' : user.status === 0 ? 'Pending' : user.status === 2 ? 'Rejected' : 'Unknown'}
                    </div>
                  </div>
                  <div className="user-card-meta">
                    <span>Role: {user.role === 2 ? 'Admin' : user.role === 1 ? 'User' : 'Applicant'}</span>
                    <span>Joined: {new Date(user.createdAt).toLocaleDateString()}</span>
                  </div>
                  {user.status !== 3 && (
                    <div className="user-card-actions">
                      {user.status === 1 && (
                        <button
                          className="btn-modern btn-primary-modern btn-sm"
                          onClick={() => handleImpersonateUser(user)}
                          disabled={isActioning}
                          title="Login as this user to manage their charging/queue"
                        >
                          <LogIn size={14} />
                          Impersonate
                        </button>
                      )}
                      <button
                        className="btn-modern btn-destructive-modern btn-sm"
                        onClick={() => handleDeleteUser(user.id, 'Deleted by admin')}
                        disabled={isActioning}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Chargers Tab */}
      {activeTab === 'chargers' && (
        <div className="modern-admin-section">
          <div className="modern-section">
            <div className="section-header">
              <h3 className="section-title">
                Charger Management
                <span className="count-badge">{filteredChargers.length}</span>
              </h3>
              <button className="btn-modern btn-primary-modern">
                <Plus size={16} />
                Add Charger
              </button>
            </div>

            <div className="modern-cards-grid">
              {filteredChargers.map((charger) => (
                <div key={charger.id} className={`modern-charger-card ${charger.online ? 'online' : 'offline'}`}>
                  <div className="charger-card-header">
                    <div className="charger-icon">
                      <Zap size={20} />
                    </div>
                    <div className="charger-basic-info">
                      <h4 className="charger-name">{charger.name}</h4>
                      <p className="charger-details">Slot #{charger.slotNumber} • {charger.status}</p>
                    </div>
                    <div className={`charger-status-badge ${charger.online ? 'online' : 'offline'}`}>
                      {charger.online ? 'Online' : 'Offline'}
                    </div>
                  </div>
                  <div className="charger-card-meta">
                    <span>Queue: {charger.queueUserIds.length}/{charger.maxQueueLength}</span>
                    <span>Updated: {new Date(charger.lastChangeAt).toLocaleString()}</span>
                  </div>
                  <div className="charger-card-actions">
                    <button
                      className={`btn-modern ${charger.online ? 'btn-warning-modern' : 'btn-success-modern'}`}
                      onClick={() => handleToggleChargerStatus(charger.id, charger.online)}
                      disabled={isActioning}
                    >
                      {charger.online ? 'Disable' : 'Enable'}
                    </button>
                    <button className="btn-modern btn-secondary-modern">
                      <Settings size={16} />
                      Configure
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sessions Tab */}
      {activeTab === 'sessions' && (
        <div className="modern-admin-section">
          <div className="modern-empty-state">
            <div className="empty-icon">
              <Clock size={64} />
            </div>
            <h3 className="empty-title">Session Management</h3>
            <p className="empty-description">Active session monitoring and management coming soon...</p>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <QueueAccessSettingsPanel onNotify={(message, type) => setNotification({ message, type })} />
      )}

      {isLoading && (
        <div className="modern-loading-overlay">
          <div className="loading-content">
            <RefreshCw className="animate-spin" size={32} />
            <p>Loading admin data...</p>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`modern-notification ${notification.type}`}>
          <div className="notification-icon">
            {notification.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
          </div>
          <span className="notification-message">{notification.message}</span>
          <button 
            className="notification-close"
            onClick={() => setNotification(null)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}